import PaytoIcon from 'assets/icons/payments/payto-icon-2.svg';
import SizeBox from 'components/common/SizeBox';
import {
  DESKTOP_MIN_WIDTH,
  FUNC_DEFAULT,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import { PAYTO_LOG_TAG } from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import usePaytoGuideline from 'features/to/hooks/usePaytoGuideline';
import { timer } from 'helpers';
import useCountdownToActionVer3 from 'hooks/useCountdownToActionVer3';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import {
  cursorPointerMixin,
  displayFlexColCenterCenterMixin,
  displayFlexRowCenterCenterMixin,
  fontSize14MixinObject,
  paragraph600Mixin,
  paragraph700Mixin,
  paragraphMixin,
  preventFontJumpyMixin,
} from 'styles';

const COUNTDOWN_INITIAL = 300; // 5 minutes
// const COUNTDOWN_INITIAL = 90; // 15 secs
const COUNTDOWN_CHECK_STATUS_INITIAL = 5; // 5 secs

const Popup = ({
  isOpen,
  buttonLabel1,
  onClose,
  buttonLabel2,
  onClick,
  isBackdropOverlap,
  zIndexBackdropOverlap,
  zIndexModal = 0,
  onShowWatchPaytoGuideline,
  onPaytoAccountAuthoriseProcessingTimeout,
  onPaytoAccountAuthoriseCheckStatus,
  isShowIframeModal,
  modalOptions,
}) => {
  const { t } = useLang();

  const { paytoAccountAuthorising } = usePaytoStore();
  const { getPaytoGuidelineUrl } = usePaytoGuideline();

  const { isEdit } = modalOptions || {};

  const targetDateTimeoutRef = useRef(Date.now());
  const isCountdownTimeoutStart = isOpen;
  const handlePaytoAccountAuthoriseProcessingTimeout = (
    countdownCallback = FUNC_DEFAULT
  ) => {
    onPaytoAccountAuthoriseProcessingTimeout(isEdit);
    countdownCallback?.();
  };
  const { totalSeconds: countdown } = useCountdownToActionVer3({
    secsInit: COUNTDOWN_INITIAL,
    nowRef: isCountdownTimeoutStart && targetDateTimeoutRef,
    onComplete: handlePaytoAccountAuthoriseProcessingTimeout,
  });

  const targetDateCheckStatusRef = useRef(Date.now());
  const isCountdownCheckStatusStart =
    isOpen && !isEmpty(paytoAccountAuthorising) && countdown > 5;
  const handlePaytoAccountAuthoriseCheckStatus = (
    countdownCallback = FUNC_DEFAULT
  ) => {
    onPaytoAccountAuthoriseCheckStatus(countdownCallback, isEdit);
  };
  useCountdownToActionVer3({
    secsInit: COUNTDOWN_CHECK_STATUS_INITIAL,
    nowRef: isCountdownCheckStatusStart && targetDateCheckStatusRef,
    isLoop: true,
    onComplete: handlePaytoAccountAuthoriseCheckStatus,
  });

  const handleOnOpenGuidelineModal = (countdown) => {
    const modalOptions = { countdown };
    console.log(
      `⭐ ${PAYTO_LOG_TAG} 👉 handleOnOpenGuidelineModal ➡️ modalOptions ↩`,
      modalOptions
    );
    // onShowWatchPaytoGuideline(modalOptions);
    window.open(getPaytoGuidelineUrl(), '_blank');
  };

  const getGuidelineDesc = () => {
    if (paytoAccountAuthorising?.bankName)
      return `${t('label_authorize_by', {
        bankName: paytoAccountAuthorising?.bankName,
      })}`;
    if (paytoAccountAuthorising?.payId) return `${t('label_authorize_by_3')}`;
    return `${t('label_authorize_by_2')}`;
  };

  return (
    <>
      <style>
        {zIndexModal &&
          `
          @media screen and (max-width: ${TABLET_MAX_WIDTH - 1}px) {
            .modal {
              z-index: ${zIndexModal || '1101'};
            }
          }
          @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
            .modal {
              z-index: ${zIndexModal || '1101'};
            }
          }
        `}
        {isBackdropOverlap
          ? `
          .authorise-payto-account {
            z-index: ${zIndexBackdropOverlap || '1100'};
            background: #212121;
            opacity: 0.7!important;
            display: initial!important;
          }
        `
          : `
        .authorise-payto-account {
          background: #212121;
          opacity: 0.7!important;
          display: initial!important;
        }
      `}
      </style>
      <ModalStyled
        show={isOpen}
        onHide={FUNC_DEFAULT}
        aria-labelledby="contained-modal-title-vcenter"
        backdropClassName="authorise-payto-account"
        centered
      >
        <ModalBody>
          <BodyContentTop>
            <img src={PaytoIcon} height={40} />
            <SizeBox height={20} />
            <Desc>{t('label_authorise_payto_account_popup_desc')}</Desc>
          </BodyContentTop>
          <SizeBox height={10} />
          <BodyContentSection>
            <SectionDesc>
              {t('label_authorise_payto_account_popup_desc_2')}
            </SectionDesc>
            <SizeBox height={20} />
            <GuidelineSection
              onClick={() => handleOnOpenGuidelineModal(countdown)}
            >
              <GuidelineDesc>{getGuidelineDesc()}</GuidelineDesc>
              {!isShowIframeModal && (
                <YoutubeIconStyled src={YoutubeIcon} height={24} width={24} />
              )}
            </GuidelineSection>
            <SizeBox height={20} />
            <TimeRemainingSection>
              <TimeRemainingDesc>
                {`${t('label_time_remaining')}:`}
                {` `}
                <span>{`${timer.convertSecondsToMinuteSecondString(
                  countdown
                )}`}</span>
              </TimeRemainingDesc>
            </TimeRemainingSection>
            <SizeBox height={20} />
            <SectionDesc
              style={{
                ...fontSize14MixinObject,
                color: 'var(--ds-c-grey-neutral)',
              }}
            >
              {t('label_authorise_payto_account_popup_note')}
            </SectionDesc>
          </BodyContentSection>
        </ModalBody>
      </ModalStyled>
    </>
  );
};

const ModalStyled = styled(Modal)`
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100dvh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 0px 16px;
        border-radius: 0px;
        box-shadow: none;
        background: var(--ds-c-white);
        width: 100%;
        height: fit-content;
        margin-inline: auto;
        border: none;
        border-radius: 8px 8px 0 0;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      & .modal-content {
        padding: 20px;
        border-radius: 20px;
        box-shadow: var(--ds-bs-4);
        background: var(--ds-c-white);
        width: 476px;
        height: fit-content;
        margin-inline: auto;
        border: none;
      }
    }
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  text-align: left;
  margin-bottom: 16px;
  padding-top: 16px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 0px;
    padding-top: unset;
  }
`;
const BodyContentTop = styled.div`
  ${displayFlexColCenterCenterMixin};
`;
const BodyContentSection = styled.div``;

const Desc = styled.p`
  ${paragraphMixin};
  font-size: 20px;
  text-align: center;
`;
const SectionDesc = styled.p`
  ${paragraphMixin};
  font-weight: 500;
  text-align: center;
`;

const GuidelineSection = styled.div`
  width: fit-content;
  min-height: 44px;
  border-radius: 100px;
  padding-inline: 16px;
  margin-inline: auto;
  border: 1px solid var(--ds-c-blue-hyperlink-default);
  ${cursorPointerMixin};
  ${displayFlexRowCenterCenterMixin};
`;
const GuidelineDesc = styled.p`
  ${paragraph600Mixin};
  color: var(--ds-c-blue-hyperlink-default);
`;

const YoutubeIconStyled = styled.img`
  margin-left: 4px;
  object-fit: contain;
`;

const TimeRemainingSection = styled.div`
  ${displayFlexRowCenterCenterMixin};
  gap: 4px;
  height: 20px;
`;
const TimeRemainingDesc = styled.p`
  ${paragraph700Mixin};
  & span {
    font-family: var(--ff-tertiary);
    font-size: 20px;
    line-height: 24px;
    font-weight: 700;
    ${preventFontJumpyMixin};
  }
`;

export default Popup;
